import t from '@jetshop/intl';
import { styled } from 'linaria/react';
import React, { useEffect } from 'react';
import MaxWidth from '../../Layout/MaxWidth';
import { theme } from '../../Theme';
import CategoryHeaderLoadingState from '../CategoryHeaderLoadingState';
import ProductGrid from '../ProductGrid';
import contentComponents from '../../CustomContent/elementMap';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import { default as Breadcrumbs } from '@jetshop/ui/Breadcrumbs';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import ArticleContainer from '../../CustomContent/Common/ArticleContainer';
import { useQuery } from 'react-apollo';
import categoryContentQuery from '../../CustomContent/CategoryContentQuery.gql';
import { TrackPageLoad } from '../../DataLayer/DataLayer';

const ArticleWrapper = styled('div')`
  max-width: 90rem;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  padding: 0 2rem;

  ${theme.below.lg} {
    display: block;
  }

  ${theme.below.sm} {
    padding: 0rem 1.25rem;
  }
`;

const Header = styled('div')`
  width: 22rem;
  padding: 2.5rem 0 0;

  ${theme.below.lg} {
    width: 100%;
  }

  ${theme.below.sm} {
    padding: 2rem 0 0;
  }
`;

const MainImage = styled('div')`
  padding: 0 0 4rem;

  ${theme.below.sm} {
    padding: 0 0 2rem;
  }

  > div {
    padding: 50% 0 0;

    ${theme.below.sm} {
      padding: 100% 0 0;
    }
  }
`;

const Content = styled(ArticleContainer)`
  max-width: 45rem;
  padding-top: 3.5rem;

  &::after {
    content: '';
    display: block;
    clear: both;
  }

  ${theme.below.lg} {
    padding-top: 0rem;
  }
`;

const ProductListingContainer = styled(MaxWidth)`
  padding: 0;
  max-width: 45rem;
  margin: 4rem 0;

  h2 {
    font-size: 26px;
    margin: 0 0 2rem;
  }

  .product-grid {
    li {
      width: 50%;
    }
  }

  ${theme.below.sm} {
    h2 {
      font-size: 22px;
    }

    .product-grid {
      li {
        width: 100%;
      }
    }
  }
`;

const ContentContainer = styled('div')`
  padding-top: 2rem;
  width: calc(100% - 26rem);
  display: block;

  ${theme.below.lg} {
    width: 100%;
  }

  ${theme.below.sm} {
    padding-top: 0.5rem;
  }
`;

const CategoryWrapper = styled('div')``;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  ul& {
    margin-bottom: 36px;
    flex-wrap: nowrap;

    ${theme.below.md} {
      display: none;
    }
  }

  ul& > li,
  > li a {
    font-size: 1rem;
    color: ${theme.colors.black};
  }

  > li a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  > li:last-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &::after {
      display: none;
    }
  }
`;

const ProductNumber = styled('p')`
  text-transform: uppercase;
  font-size: 12px;
  color: #707070;
  letter-spacing: 0.5px;
  margin-top: 2rem;
  margin-bottom: 0.5rem;
`;

const CategoryName = styled('h1')`
  font-size: 40px;
  margin-bottom: 16px;

  ${theme.below.sm} {
    font-size: 34px;
  }
`;

const ContactInfo = styled('aside')`
  margin-top: 3rem;
  line-height: 1.6;
  font-size: 16px;

  ${theme.below.sm} {
    line-height: 1.8;
    font-size: 14px;
  }

  h2 {
    margin: 0 0 1rem;
  }

  p {
    line-height: 1.6;
    font-size: 16px;

    ${theme.below.sm} {
      font-size: 14px;
      line-height: 1.8;
      font-size: 14px;
    }

    + p {
      margin-top: 1.25rem;
    }
  }

  a {
    color: ${theme.colors.black};
  }
`;

const LinkListWrapper = styled('section')``;

const Category = ({ category, result, transparencyContext }) => {
  useEffect(() => {
    if (!transparencyContext.headerIsOpaque) {
      transparencyContext.setHeaderAsOpaque(true);
    }
  });

  const contactContentQuery = useQuery(categoryContentQuery, {
    variables: {
      id: theme.settings.systemsContactCategoryId
    }
  });
  let contactContentItems = contactContentQuery.data?.category?.data?.items;

  const linkListContentQuery = useQuery(categoryContentQuery, {
    variables: {
      id: theme.settings.systemsLinkListCategoryId
    }
  });
  let linkListContentItems = linkListContentQuery.data?.category?.data?.items;

  if (!category) {
    return (
      <CategoryWrapper>
        <CategoryHeaderLoadingState />;
      </CategoryWrapper>
    );
  }

  const breadcrumbProps = {
    breadcrumbText: category.breadcrumbText,
    parents: result?.data?.route?.parents
  };

  const contentItems = category.data.items;

  // console.log(contentItems, " - contentItems");
  // console.log(transparencyContext, " - transparencyContext");

  return (
    <CategoryWrapper>
      <TrackPageLoad pageType={'Fläktsystem'} route={category.primaryRoute} />
      <ArticleWrapper>
        <Header>
          <Above breakpoint={'lg'}>
            <StyledBreadcrumbs {...breadcrumbProps} />
          </Above>
          <CategoryName data-testid="page-header">{category.name}</CategoryName>
          <Above breakpoint={'lg'}>
            <ContactInfo>
              {contactContentItems && (
                <ContentRenderer
                  items={contactContentItems}
                  components={contentComponents}
                />
              )}
            </ContactInfo>
          </Above>
        </Header>

        <ContentContainer>
          <Content>
            <ContentRenderer
              items={contentItems}
              components={contentComponents}
            />
          </Content>

          {category.products.totalResults > 0 && (
            <ProductListingContainer>
              <h2>{t('Related products')}</h2>
              <ProductGrid
                products={category.products.result}
                listName={'system-article'}
                categoryPath={category.isDynamic ? null : result?.data?.route}
                loading={result.loading}
                imageSizes={[1, 1 / 2, 1 / 2, 1 / 2]}
              />
            </ProductListingContainer>
          )}
          <Below breakpoint={'lg'}>
            <ContactInfo>
              {contactContentItems && (
                <ContentRenderer
                  items={contactContentItems}
                  components={contentComponents}
                />
              )}
            </ContactInfo>
          </Below>
        </ContentContainer>
      </ArticleWrapper>
      <LinkListWrapper>
        {linkListContentItems && (
          <ContentRenderer
            items={linkListContentItems}
            components={contentComponents}
          />
        )}
      </LinkListWrapper>
    </CategoryWrapper>
  );
};

export default Category;
